import { useRef, useCallback, useEffect, useMemo } from "react";
/**
 * CustomUpload Component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child elements to be clicked for file selection
 * @param {Function} props.onChange - Callback function called with selected files array
 * @param {boolean} [props.multiple=true] - Allow multiple file selection
 * @param {string} [props.accept="*"] - File types to accept (e.g., "image/*", ".pdf", etc.)
 * @param {boolean} [props.disabled=false] - Disable file selection
 */

const CustomUpload = (props) => {
  const {
    children,
    onChange,
    multiple = true,
    accept = "*",
    disabled = false,
    ...inputProps
  } = props;
  const inputRef = useRef(null);

  const handleClick = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleFileChange = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      onChange?.(files);

      e.target.value = "";
    },
    [onChange],
  );

  const cursorStyle = useMemo(
    () => (disabled ? "not-allowed" : "pointer"),
    [disabled],
  );

  useEffect(() => {
    return () => {
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };
  }, []);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        onChange={handleFileChange}
        style={{ display: "none" }}
        {...inputProps}
      />

      <span
        onClick={handleClick}
        style={{
          cursor: cursorStyle,
          display: "inline-flex",
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {children}
      </span>
    </>
  );
};

export default CustomUpload;
